import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, DropdownProps, Grid, Header, Icon } from 'semantic-ui-react';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import { BillStatus, BillType, DocumentType } from 'ts-types/api.enums';
import {
  DocumentCopiesRequest,
  DocumentCopiesSearchResponse,
  DocumentCopyResponse,
  PatientDto,
  SelectedDocumentCopyDto
} from 'ts-types/api.types';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import moment from 'moment';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { FormApi } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { noop } from 'utils/functionUtils';
import Input from 'components/final-form/Input';
import FormContent from 'components/layout/FormContent';
import FormContainer from 'components/layout/FormContainer';
import PxGrid from 'components/layout/PxGrid';
import { PxDropdownSc } from 'components/final-form/Select';
import {
  documentCopiesOutput,
  getDocumentCopyPdfUrl,
  searchDocumentCollectiveBills
} from 'services/documentCopiesServices';
import { composeValidators, mustBeNumber, mustBePositiveNumber } from 'utils/validatorUtils';
import { ensureOnlyNumericInput } from 'utils/filterUtils';
import PdfModal from 'components/pdf-modal/PdfModal';
import { applyStyles } from 'utils/textUtils';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34,36,38,.15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(6) {
    text-align: right;
    padding-right: 0.5rem;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(7) {
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34,36,38,.15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const TableContainer = styled.div`
  height: 90%;
  min-height: 300px;
`;

const StyledForm = styled.form`
  height: 100%;
`;

const StyledFormContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`
   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-top: 2.2rem;
    margin-left: 20px;
  }

  .header-description {
    margin-bottom: 0rem;

    .header-button {
      margin-left: 1rem;
      vertical-align: top;
      background: transparent;
    }
  }

  .header-data {
    margin-top: 0.2rem;
    font-weight: 300;
  }
`;

const StyledSelectedBills = styled.span`
  float: right;
  text-align: right;
  font-weight: normal;
  font-size: 14px;

  &.margin-top {
    margin-top: 0.5em;
  }

  .highlight-value {
      color: rgb(48 145 218);
      font-weight: bold;
  }
`;

const FormBottomContent = styled(FormContent)`
  flex: 0 0 auto;
`;

const StyledCheckBox = styled(CheckboxSc)`
  margin-top: 0;
`;

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

interface ExtendedDocCopiesSearchResponse extends DocumentCopiesSearchResponse {
  selected: boolean,
  currentPaidAmount: number
}

interface FinalFormData {
  bills: Array<ExtendedDocCopiesSearchResponse>,
  selected?: ExtendedDocCopiesSearchResponse,
  copy: boolean,
  exemplare: number,
  documentType: DocumentType,
  separateDocuments: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  bills: Array<ExtendedDocCopiesSearchResponse>,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  onFilterBills: (documents: Array<DocumentCopiesSearchResponse>) => void,
  onBillsLoadingChange: (documentsLoading: boolean) => void
}

interface State {
  cancelTokenSource: CancelTokenSource,
  selectedDocumentType: DocumentType,
  formData: Partial<FinalFormData>,
  documentTypes: Array<DropdownOption>,
  documentCopiesOutput: Array<DocumentCopyResponse>
  documentCopiesPdfModalOpen: boolean
}

class DocumentCopiesTableSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource: cancelTokenSource,
      documentTypes: this.getDocumentTypes(),
      selectedDocumentType: DocumentType.BILL,
      documentCopiesOutput: [],
      documentCopiesPdfModalOpen: false,
      formData: {
        copy: true,
        exemplare: 1
      }
    };

  }

  getDocumentTypes = (): Array<DropdownOption> => {

    const {getMessage} = this.props;

    return Object.values(DocumentType)
        .map((docType, index): DropdownOption => (
            {
              key: index,
              text: docType === DocumentType.BILL
                  ? getMessage("bill")
                  : `${index}. ${getMessage("bill.reminder")}`,
              value: docType
            }
        ));
  };

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData, selectedDocumentType} = this.state;
    const {bills, billsLoading} = this.props;

    if (
        (prevBillsLoading != billsLoading && !billsLoading)
        || (prevState.selectedDocumentType != selectedDocumentType)
    ) {

      const mappedBills: Array<ExtendedDocCopiesSearchResponse> = bills.map(bill => {

        const calculatedPaidAmount = ((bill.amount + bill.reminderSurcharge) - bill.paidAmount).toFixed(2);

        const selected = bill.status !== BillStatus.STORNO
            && bill.type === BillType.NORMAL
            && (selectedDocumentType === DocumentType.BILL
                || bill.documentType === selectedDocumentType);

        return {
          ...bill,
          selected: selected,
          currentPaidAmount: parseFloat(calculatedPaidAmount)
        }
      });

      this.setState({
        formData: {
          ...formData,
          bills: mappedBills,
          selected: undefined
        }
      })
    }
  }

  openDocumentCopiesPdfModal = (documentCopyResponses: Array<DocumentCopyResponse>) => {
    this.setState({
      documentCopiesPdfModalOpen: true,
      documentCopiesOutput: documentCopyResponses
    })
  };

  closeDocumentCopiesPdfModal = () => {

    this.setState({
      documentCopiesPdfModalOpen: false,
      documentCopiesOutput: []
    });
  };

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {currentUser} = this.props;
    const {selectedDocumentType, cancelTokenSource} = this.state;
    const {bills, separateDocuments} = values;

    if (bills && bills.length > 0) {
      const selectedBills: Array<SelectedDocumentCopyDto> = [];

      const filteredBills = bills.filter(bill => bill.selected);
      filteredBills.forEach((bill) => {

        const selectedBill: SelectedDocumentCopyDto = {
          billId: parseInt(bill.billId, 10),
          documentType: bill.documentType
        };

        selectedBills.push(selectedBill);
      });

      if (selectedBills.length != 0) {
        const documentCopiesRequest: Partial<DocumentCopiesRequest> = {
          documentTypeFilter: selectedDocumentType,
          copy: values.copy,
          separateDocuments: separateDocuments,
          language: currentUser ? currentUser.userConfig.language : "de",
          bills: selectedBills
        };

        await documentCopiesOutput(documentCopiesRequest, cancelTokenSource)
            .then(response => {
              if (response.length > 0) {
                this.openDocumentCopiesPdfModal(response);
              }
            })
            .catch(noop);
      }
    }
  };

  onDocumentTypeChange = (documentType: any, form: FormApi) => {
    this.setState({
      selectedDocumentType: documentType
    });
  };

  showCollectiveBill = (bill: ExtendedDocCopiesSearchResponse, collectiveBill: boolean) => {
    const {currentUser, onFilterBills, onBillsLoadingChange} = this.props;
    const {cancelTokenSource} = this.state;

    const mandateId = currentUser ? currentUser.userConfig.mandateId : undefined;
    const billId = collectiveBill ? parseInt(bill.billId, 10) : bill.collectiveBillId ;

    onBillsLoadingChange(true);

    searchDocumentCollectiveBills(billId, mandateId, cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .finally(() => {
          onBillsLoadingChange(false);
        });
  };

  billSelectionEnabled = (bill: ExtendedDocCopiesSearchResponse): boolean => {
    const {selectedDocumentType} = this.state;

    if (bill.status === BillStatus.STORNO) {
      return false;
    }

    if (selectedDocumentType === DocumentType.BILL) {
      return true;
    }

    return selectedDocumentType === bill.documentType;
  };

  updateBillSelection = (bill: ExtendedDocCopiesSearchResponse, index: number, form: FormApi) => {
    if (bill.status != BillStatus.STORNO) {

      const modifiedBill: ExtendedDocCopiesSearchResponse = {
        ...bill,
        selected: !bill.selected
      };

      form.change(`bills[${index}]`, modifiedBill);
    }
  };

  billSelectCellRenderer = (data: any) => {

    const {rowIndex, rowData} = data;

    return (
        <Field
            name={`bills[${rowIndex}].selected`}
            component={CheckBox}
            disabled={!this.billSelectionEnabled(rowData)}
        />
    )
  };

  billStatusCellRenderer = (data: any) => {

    const rowData: ExtendedDocCopiesSearchResponse = data.rowData;

    const billIconRequest: BillIconRequest = {
      hasReminders: false,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  billDateCellRenderer = (data: any) => <div>{moment(data.rowData.date).format("DD.MM.YYYY")}</div>;
  amountCellRenderer = (data: any) => <div>{(data.rowData.amount)}</div>;
  paidAmountCellRenderer = (data: any) => <div>{(data.rowData.paidAmount)}</div>;

  paymentDateCellRenderer = (data: any) => {
    if (data.rowData.lastPaymentDate) {
      return (
          <div>{moment(data.rowData.lastPaymentDate).format("DD.MM.YYYY")}</div>
      )
    }
  };

  billsRowGetter = (bills: Array<ExtendedDocCopiesSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  selectHeaderColumn = (form: FormApi, bills: Array<ExtendedDocCopiesSearchResponse>) => {

    const {getMessage} = this.props;

    const countSelectableBills = bills.filter(bill => this.billSelectionEnabled(bill)).length;
    const countSelectedBills = bills.filter(bill => bill.selected).length;
    const indeterminate = bills.length > 0 && countSelectedBills > 0 && countSelectableBills !== countSelectedBills;
    const noSelectedBills = countSelectedBills === 0 && countSelectableBills > 0;
    const allBillsSelected = countSelectableBills > 0 && countSelectedBills === countSelectableBills;

    return (
        <StyledCheckBox
            checked={countSelectedBills > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedBills || indeterminate) {
                  const updateBills = bills.map(bill => {

                    if (this.billSelectionEnabled(bill)) {
                      return {
                        ...bill,
                        selected: true
                      };
                    }

                    return bill;
                  });

                  form.change("bills", updateBills);
                }

                if (allBillsSelected) {
                  const updateBills = bills.map(bill => {
                    return {
                      ...bill,
                      selected: false
                    };
                  });

                  form.change("bills", updateBills);
                }

              }
            }
            label={getMessage("select")}
            disabled={bills.length === 0 || countSelectableBills === 0}
        />
    )
  };

  billsRowRenderer = (form: FormApi) => ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    const {bills = [], selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style, userSelect: "none"};

    if (selected && bills[index].billId == selected.billId) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
            onDoubleClick={() => this.billSelectionEnabled(bills[index])
                ? this.updateBillSelection(bills[index], index, form)
                : noop()}
        >
          {columns}
        </div>
    );
  };

  render(): React.ReactNode {

    const {formData} = this.state;

    return (
        <StyledFinalFormContainer>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
        </StyledFinalFormContainer>
    )
  };

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {bills = [], selected} = values;
    const {billsLoading, getMessage, getMessageWithNamedParams} = this.props;
    const {documentTypes, selectedDocumentType, documentCopiesPdfModalOpen, documentCopiesOutput} = this.state;

    const countSelectedBills = bills.filter((bill: ExtendedDocCopiesSearchResponse) => bill.selected).length;
    const outputBtnDisabled = submitting || bills.length === 0 || countSelectedBills === 0;
    const billLength = bills.length;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <StyledFormContent>
                <StyledContainer style={{width: "50%"}}>
                  <Header>
                    {getMessage("bills")}
                    {!outputBtnDisabled &&

                    <StyledSelectedBills>
                      {
                        applyStyles(getMessage("documentCopies.selectedDocuments")
                            .replace("{{countSelectedBills}}", "" + countSelectedBills)
                        .replace("{{billsLength}}","" + billLength)
                        )
                      }
                    </StyledSelectedBills>
                    }
                  </Header>
                  <TableContainer>
                    {billsLoading
                        ? <LoaderComponent
                            message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                        : this.renderBillsTable(form, bills)}
                  </TableContainer>
                </StyledContainer>
                <StyledContainer style={{width: "50%"}} className="margin">
                  <Header className={"header-description"}>
                    {getMessage("patient")}
                  </Header>
                  {selected && selected.patientName &&
                  <Header as="h4" className={"header-data"}>
                    <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                    <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                  </Header>
                  }
                  <Header className={"header-description"}>
                    {getMessage("guarantor")}
                  </Header>
                  {selected && selected.guarantorDescription &&
                  <Header as="h4" className={"header-data"}>
                    <div>{`${selected.guarantorDescription}`}</div>
                    <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                  </Header>
                  }
                  <Header className={"header-description"}>
                    {getMessage("bill.bank")}
                  </Header>
                  {selected && selected.bankInfo &&
                  <Header as="h4" className={"header-data"}>
                    <div>{`${selected.bankInfo}`}</div>
                  </Header>
                  }
                  {selected && (selected.type === BillType.COLLECTIVE || selected.type === BillType.PART_OF_COLLECTIVE) &&
                      <React.Fragment>
                        <Header className={"header-description"}>
                          {selected.type === BillType.COLLECTIVE &&
                            getMessage("documentCopies.showCollectiveBill")}
                          {selected.type === BillType.PART_OF_COLLECTIVE &&
                            getMessageWithNamedParams(
                              "documentCopies.showPartOfCollectiveBill.collectiveBillNumber",
                              {collectiveBillId: selected.collectiveBillId})}
                          <Button
                              className="header-button"
                              size="small"
                              type="button"
                              icon
                              onClick={() => this.showCollectiveBill(selected, selected.type === BillType.COLLECTIVE)}
                          >
                            <Icon
                                name="external share"
                                size="small"
                            />
                          </Button>
                        </Header>
                      </React.Fragment>
                  }
                </StyledContainer>
              </StyledFormContent>
            </FormContent>
            <FormBottomContent>
              <PxGrid>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("documentCopies.document")}:
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <PxDropdownSc
                        value={selectedDocumentType}
                        onChange={
                          (e: any, {value}: DropdownProps) =>
                              this.onDocumentTypeChange(value, form)
                        }
                        options={documentTypes}
                        selection
                        fluid
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <Field
                        name="copy"
                        component={CheckBox}
                        label={getMessage("copy")}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    {getMessage("documentCopies.exemplare")}:
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <Field
                        name="exemplare"
                        component={Input}
                        onKeyDown={ensureOnlyNumericInput}
                        validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                        fluid
                    />
                  </Grid.Column>
                  <Grid.Column width={2}>
                    <Field
                        name="separateDocuments"
                        component={CheckBox}
                        label={getMessage("documentCopies.separateDocuments")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </FormBottomContent>
            <FormButtonsRow>
              <Button compact type="submit" primary disabled={outputBtnDisabled}>
                {getMessage("output")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
          {
            documentCopiesPdfModalOpen &&
            <PdfModal
                modalOpen={documentCopiesPdfModalOpen}
                onCloseModal={this.closeDocumentCopiesPdfModal}
                pdfResponses={documentCopiesOutput}
                getPdfUrl={this.getDocumentCopiesPdfUrl}
            />
          }
        </StyledForm>
    );
  };

  getDocumentCopiesPdfUrl = (response: DocumentCopyResponse) => {
    return getDocumentCopyPdfUrl(response.pdfId, response.pdfName);
  };

  renderBillsTable = (form: FormApi, bills: Array<ExtendedDocCopiesSearchResponse>): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form)}
            rowCount={bills.length}
            columns={[
              {
                width: 100,
                label: this.selectHeaderColumn(form, bills),
                dataKey: "selected",
                cellRenderer: this.billSelectCellRenderer
              },
              {
                width: 80,
                label: getMessage("bill"),
                dataKey: "billStatus",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 120,
                label: getMessage("bill.date"),
                dataKey: "billDate",
                cellRenderer: this.billDateCellRenderer
              },
              {
                width: 90,
                label: "#",
                dataKey: "billId"
              },
              {
                width: 120,
                label: getMessage("bill.paymentDate"),
                dataKey: "billId",
                cellRenderer: this.paymentDateCellRenderer
              },
              {
                width: 130,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.amountCellRenderer
              },
              {
                width: 100,
                label: getMessage("bill.paidAmount"),
                dataKey: "paidAmount",
                cellRenderer: this.paidAmountCellRenderer
              }
            ]}
        />
    )
  };
}

export default withAuthContext(withMessages(DocumentCopiesTableSegment));
